import { AfterViewInit, Component, computed, ElementRef, inject, viewChild } from '@angular/core';
import { SideNavService } from './side-nav.service';
import { SsApiService } from '../../../services/api/ss-api.service';
import { PlatformService } from '../../../services/platform.service';
import { UserService } from '../../../services/user/user.service';
import { BadgeService } from '../../../services/badge.service';
import { LanguageService } from '../../../services/language/language.service';
import { EnvironmentService } from '../../../services/environment.service';
import { GoogleTagManagerService } from '../../../services/google-tag-manager.service';
import { TranslationService } from '../../translation/translation.service';
import { Router, RouterLink } from '@angular/router';
import { delay, filter, finalize, first, tap } from 'rxjs/operators';
import { ServiceWorkerService } from '../../../services/service-worker.service';
import { SeoService } from '../../../services/seo.service';
import { JackpotService } from '../../../../page/jackpot/jackpot.service';
import { WrSocketService } from '../../../services/wr-socket.service';
import { of } from 'rxjs';
import { ZendeskChatService } from '../../../services/zendesk/zendesk-chat.service';
import { GroupsService } from '../../../services/groups.service';
import { MotivateWithdrawalService } from '../../../services/motivate-withdrawal.service';
import { LotteryService } from '../../../services/lottery.service';
import { UntilDestroy } from 'ngx-unificator/decorator';
import { untilDestroyed } from 'ngx-unificator/rxjs';
import { ArabicService } from '../../../services/arabic.service';
import { TranslatePipe } from '../../translation/translate.pipe';
import { CurrencyFormatPipe } from '../../pipes/currency-format.pipe';
import { RouterLinkDirective } from '../../directives/router-link.directive';
import { SetImgAltDirective } from '../../directives/set-img-alt.directive';
import { AppShellRenderSSRDirective } from '../../directives/app-shell/app-shell-render-ssr.directive';
import { AsyncPipe, Location, NgClass } from '@angular/common';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { BonusStoreService } from '../../../services/bonus-store.service';
import { InstallAppService } from '../../../services/install-app.service';
import { LOCAL_STORAGE_FRAUD_KEY_DEPOSIT } from '../../../services/fraud-check.service';
import { LocalstorageService } from 'ngx-unificator/services';
import { AccordionDirective, AppShellNoSSRDirective } from 'ngx-unificator/directives';
import { ROOT_LANG } from '../../../helpers/router';
import { RestrictSignupService } from '../../../services/restrict-signup.service';
import { XmasService } from '../../../../page/promo/xmas/xmas.service';
import { HalloweenService } from '../../../../page/promo/halloween/halloween.service';
import { LottieComponent } from '../../standalone/lottie/lottie.component';

@UntilDestroy()
@Component({
    selector: 'app-side-nav',
    templateUrl: './side-nav.component.html',
    styleUrls: ['./side-nav.component.scss'],
    imports: [
        ClickOutsideDirective,
        NgClass,
        AppShellRenderSSRDirective,
        SetImgAltDirective,
        RouterLink,
        RouterLinkDirective,
        AccordionDirective,
        AsyncPipe,
        CurrencyFormatPipe,
        TranslatePipe,
        LottieComponent,
        AppShellNoSSRDirective,
    ]
})
export class SideNavComponent implements AfterViewInit {
  private _ssApi = inject(SsApiService);
  private _platform = inject(PlatformService);
  private _gtm = inject(GoogleTagManagerService);
  private _storage = inject(LocalstorageService);
  private _translationService = inject(TranslationService);
  private _router = inject(Router);
  private _wrSocket = inject(WrSocketService);
  private _location = inject(Location);
  sidebar = inject(SideNavService);
  restrictSignUp = inject(RestrictSignupService);
  user = inject(UserService);
  lang = inject(LanguageService);
  zendesk = inject(ZendeskChatService);
  swPwa = inject(ServiceWorkerService);
  badge = inject(BadgeService);
  env = inject(EnvironmentService);
  seo = inject(SeoService);
  jackpot = inject(JackpotService);
  groups = inject(GroupsService);
  motivateWithdrawal = inject(MotivateWithdrawalService);
  lottery = inject(LotteryService);
  ar = inject(ArabicService);
  bonusStoreService = inject(BonusStoreService);
  installApp = inject(InstallAppService);
  xmas = inject(XmasService);
  halloween = inject(HalloweenService);


  readonly accordion = viewChild('accordion', { read: AccordionDirective });
  readonly nav = viewChild<ElementRef>('nav');
  protected readonly ROOT_LANG = ROOT_LANG;

  public currentPath = computed(() => {
    const fullPath = this._location.path();
    const arrPath = fullPath.split('/');
    const isRootLang = this.lang.current === ROOT_LANG;
    let pathWithoutLang: string;
    if (isRootLang) {
      pathWithoutLang = arrPath.slice(1).join('/');
    } else {
      pathWithoutLang = arrPath.slice(2).join('/');
    }
    if (!pathWithoutLang) {
      return '';
    }
    return pathWithoutLang;
  });

  ngAfterViewInit() {
  }

  /**
   * Change UI language
   *
   * @param lang
   */
  public changeLanguage(lang: string) {
    let url = '';
    if (lang === ROOT_LANG) {
      url = this._router.url.replace(`/${this.lang.current}`, '');
    } else {
      if (this.lang.current === ROOT_LANG) {
        url = `/${lang}` + this._router.url;
      } else {
        url = this._router.url.replace(`/${this.lang.current}`, `/${lang}`);
      }
    }
    if (url.length > 1 && url.endsWith('/')) {
      url = url.slice(0, -1);
    }
    this.lang.changeLang(lang);
    this._router.navigateByUrl(`/${url}`, { replaceUrl: true })
      .then(() => {
        this._translationService.loadTranslates().pipe(first()).subscribe();
      });
  }

  /**
   * User logout
   */
  logout() {
    this._storage.clearItem(LOCAL_STORAGE_FRAUD_KEY_DEPOSIT);
    this._wrSocket.sendEventLogout();
    this.sidebar.close();
    this.zendesk.setInitFunctionalData();
    this._ssApi.usersSignOut().pipe(
      filter(() => this._platform.isBrowser),
      finalize(() => {
        window.location.href = '/';
      })
    ).subscribe();
  }

  installAppClick() {
    this.sidebar.close();
    this._gtm.installAppClick('app_sidebar', 'sidebar');
    this.swPwa.addAppToHomeScreen();
  }

  liveChatClick() {
    this.sidebar.close();
    this._gtm.onLiveChatClick('live_chat', 'chat');
  }

  zendeskLiveChatClick() {
    this.sidebar.close();
    this.zendesk.toggle();
    this._gtm.onLiveChatClick('live_chat', 'chat');
  }

  onLoginClick() {
    this._gtm.signInSidebarClick('login_sidebar', 'sidebar');
    this.sidebar.close();
  }

  onRegisterClick() {
    this._gtm.registerSidebarClick('register_sidebar', 'sidebar');
    this.sidebar.close();
  }

  onAccountClick() {
    this._gtm.sidebarAccountClick('login_sidebar_account', 'sidebar_account');

    if (!this.user.auth) {
      this.user.authUser();
    }

    this.sidebar.close();
  }
  onMissionsClick() {
    this.sidebar.close();
  }

  onBonusesClick() {
    this._gtm.sidebarBonusesClick('login_sidebar_bonuses', 'sidebar_bonuses');
    this.sidebar.close();
  }

  onDepositClick(isDepositClick = true) {
    if (isDepositClick) {
      this._gtm.depositSidenavClick('deposit_sidebar', 'sidebar', this.user.info.id);
    }

    this.sidebar.close();
  }

  public toTournament(routeLink) {
    if (this.user.auth) {
      this._router.navigate(routeLink).then();
    } else {
      this.user.authUser().then();
    }
  }

  public onClickSubmenu(el: HTMLElement) {
    of(true).pipe(
      untilDestroyed(this),
      delay(300),
      tap(() => {
        el.scrollIntoView({ behavior: 'smooth' });
      }),
    ).subscribe();
  }
}
